(() => {
  if (customElements.get("rr-showroom-map")) return;
  let sdkPromise;
  function showUnavailable(host) {
    host.classList.add("has-error");
    host.classList.remove("is-ready");
    host.querySelector("[data-map-status]").textContent = "";
    host.querySelector("[data-map-fallback]").hidden = false;
    const welcome = host.querySelector("[data-map-welcome]");
    welcome.classList.remove("is-leaving");
    welcome.inert = false;
    host.querySelector(".rr-showroom-map__details").hidden = false;
  }
  function loadSDK() {
    if (window.mapboxgl) return Promise.resolve();
    if (sdkPromise) return sdkPromise;
    sdkPromise = new Promise((resolve, reject) => {
      const css = document.createElement("link");
      css.rel = "stylesheet";
      css.href = "https://api.mapbox.com/mapbox-gl-js/v3.30.0/mapbox-gl.css";
      document.head.appendChild(css);
      const script = document.createElement("script");
      script.src = "https://api.mapbox.com/mapbox-gl-js/v3.30.0/mapbox-gl.js";
      script.async = true;
      const timer = setTimeout(
        () => reject(new Error("Map loading timed out")),
        20000,
      );
      script.onload = () => {
        clearTimeout(timer);
        resolve();
      };
      script.onerror = () => {
        clearTimeout(timer);
        reject(new Error("Map unavailable"));
      };
      document.head.appendChild(script);
    });
    return sdkPromise;
  }
  function mallPOIStyle(map) {
    const basemap = map
      .getStyle()
      .imports?.find((item) => item.id === "basemap");
    const style = basemap?.data;
    const paint = style?.layers?.find(
      (layer) => layer.id === "poi-label",
    )?.paint;
    if (!paint) throw new Error("POI label styling unavailable");
    const resolve = (value) => {
      if (Array.isArray(value)) {
        if (value[0] === "config")
          return (
            map.getConfigProperty("basemap", value[1]) ??
            style.schema?.[value[1]]?.default
          );
        return value.map(resolve);
      }
      if (value && typeof value === "object")
        return Object.fromEntries(
          Object.entries(value).map(([key, item]) => [key, resolve(item)]),
        );
      return value;
    };
    return {
      colorTheme: resolve(style["color-theme"]),
      paint: Object.fromEntries(
        [
          "color",
          "halo-color",
          "halo-width",
          "halo-blur",
          "emissive-strength",
        ].map((property) => [
          "icon-" + property,
          resolve(paint["text-" + property]),
        ]),
      ),
    };
  }

  function signedDistanceImage(image) {
    const { width, height, data } = image;
    const radius = 8;
    const count = width * height;
    const outer = new Float32Array(count);
    const inner = new Float32Array(count);
    for (let i = 0; i < count; i++) {
      const alpha = data[i * 4 + 3] / 255;
      outer[i] = alpha === 0 ? radius * radius : Math.max(0, 0.5 - alpha) ** 2;
      inner[i] = alpha === 1 ? radius * radius : Math.max(0, alpha - 0.5) ** 2;
    }
    const distanceTransform = (grid) => {
      const horizontal = new Float32Array(count);
      for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
          let best = radius * radius;
          for (
            let dx = Math.max(-radius, -x);
            dx <= Math.min(radius, width - x - 1);
            dx++
          )
            best = Math.min(best, grid[y * width + x + dx] + dx * dx);
          horizontal[y * width + x] = best;
        }
      }
      for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
          let best = radius * radius;
          for (
            let dy = Math.max(-radius, -y);
            dy <= Math.min(radius, height - y - 1);
            dy++
          )
            best = Math.min(best, horizontal[(y + dy) * width + x] + dy * dy);
          grid[y * width + x] = best;
        }
      }
    };
    distanceTransform(outer);
    distanceTransform(inner);
    for (let i = 0; i < count; i++) {
      const distance = Math.sqrt(outer[i]) - Math.sqrt(inner[i]);
      data[i * 4] = data[i * 4 + 1] = data[i * 4 + 2] = 255;
      data[i * 4 + 3] = Math.round(255 * (0.75 - distance / radius));
    }
    return image;
  }

  class ShowroomMap extends HTMLElement {
    connectedCallback() {
      if (this.observer) return;
      this.generation = (this.generation || 0) + 1;
      this.observer = new IntersectionObserver(
        (entries) => {
          this.inView =
            entries[0].isIntersecting && entries[0].intersectionRatio >= 0.15;
          if (this.inView && !this.started) this.start();
        },
        { threshold: [0, 0.15] },
      );
      // Measure the map itself, excluding the address and links below it.
      this.observer.observe(this.querySelector("[data-map-canvas]"));
      // Keep the GPU and tile processors idle until a phone reaches the map.
      // Desktop retains its existing preload.
      if (this.dataset.deferLoad !== "true" && !matchMedia("(max-width: 999px)").matches) this.start();
    }
    async start() {
      this.started = true;
      const generation = this.generation;
      try {
        if (!this.dataset.token || !this.dataset.token.startsWith("pk."))
          throw new Error("Missing map configuration");
        await loadSDK();
        if (!this.isConnected || generation !== this.generation) return;
        if (!window.mapboxgl.supported()) throw new Error("WebGL unavailable");
        this.cleanup = mount(this);
      } catch (error) {
        if (!this.isConnected || generation !== this.generation) return;
        showUnavailable(this);
      }
    }
    disconnectedCallback() {
      this.generation++;
      this.observer?.disconnect();
      this.observer = null;
      this.cleanup?.();
      this.cleanup = null;
      this.started = false;
      this.inView = false;
      this.classList.remove("is-ready", "has-error");
      this.querySelector(".rr-showroom-map__details").hidden = true;
      this.querySelector("[data-map-fallback]").hidden = true;
      this.querySelector("[data-map-canvas]")?.replaceChildren();
    }
  }
  // Read only polygon geometry from a vector tile's water layer.
  function oceanTileGeometry(buffer, layerName) {
    const bytes = new Uint8Array(buffer);
    let pos = 0;
    const uint = () => {
      let value = 0, shift = 0, byte;
      do {
        if (pos >= bytes.length || shift > 49) throw new Error("Invalid ocean tile");
        byte = bytes[pos++]; value += (byte & 127) * 2 ** shift; shift += 7;
      } while (byte & 128);
      return value;
    };
    const fields = (end, visit) => {
      while (pos < end) {
        const tag = uint(), wire = tag & 7, field = tag >>> 3;
        if (wire === 2) {
          const length = uint(), start = pos, stop = pos + length;
          if (stop > end) throw new Error("Invalid ocean tile length");
          visit(field, start, stop, null); pos = stop;
        } else if (wire === 0) visit(field, pos, pos, uint());
        else if (wire === 1) pos += 8;
        else if (wire === 5) pos += 4;
        else throw new Error("Unsupported ocean tile field");
      }
    };
    let result = { extent: 4096, geometries: [] };
    fields(bytes.length, (field, start, end) => {
      if (field !== 3) return;
      pos = start;
      let name = "", extent = 4096;
      const features = [];
      fields(end, (key, from, to, value) => {
        if (key === 1) name = new TextDecoder().decode(bytes.subarray(from, to));
        if (key === 5) extent = value;
        if (key === 2) features.push([from, to]);
      });
      if (name !== layerName) return;
      const geometries = [];
      for (const [from, to] of features) {
        pos = from;
        let type = 0, commands = null;
        fields(to, (key, a, b, value) => {
          if (key === 3) type = value;
          if (key === 4) commands = [a, b];
        });
        if (type !== 3 || !commands) continue;
        pos = commands[0];
        let x = 0, y = 0, ring;
        const rings = [];
        while (pos < commands[1]) {
          const command = uint(), id = command & 7, count = command >>> 3;
          if (id === 7) continue;
          if (id !== 1 && id !== 2) throw new Error("Invalid ocean geometry");
          for (let i = 0; i < count; i++) {
            const dx = uint(), dy = uint();
            x += (dx >>> 1) ^ -(dx & 1); y += (dy >>> 1) ^ -(dy & 1);
            if (id === 1) { ring = []; rings.push(ring); }
            if (!ring) throw new Error("Invalid ocean ring");
            ring.push([x, y]);
          }
        }
        geometries.push(rings);
      }
      result = { extent, geometries };
    });
    return result;
  }

  // Distance to land on a buffered water mask; tile edges are not coastlines.
  function oceanCoastalAlpha(mask, size, radius) {
    const distance = new Float32Array(size * size);
    for (let i = 0; i < distance.length; i++) distance[i] = mask[i * 4 + 3] > 127 ? radius : 0;
    for (let y = 0; y < size; y++) for (let x = 0; x < size; x++) {
      const i = y * size + x;
      if (x) distance[i] = Math.min(distance[i], distance[i - 1] + 1);
      if (y) distance[i] = Math.min(distance[i], distance[i - size] + 1);
      if (x && y) distance[i] = Math.min(distance[i], distance[i - size - 1] + Math.SQRT2);
      if (y && x + 1 < size) distance[i] = Math.min(distance[i], distance[i - size + 1] + Math.SQRT2);
    }
    for (let y = size - 1; y >= 0; y--) for (let x = size - 1; x >= 0; x--) {
      const i = y * size + x;
      if (x + 1 < size) distance[i] = Math.min(distance[i], distance[i + 1] + 1);
      if (y + 1 < size) distance[i] = Math.min(distance[i], distance[i + size] + 1);
      if (x + 1 < size && y + 1 < size) distance[i] = Math.min(distance[i], distance[i + size + 1] + Math.SQRT2);
      if (x && y + 1 < size) distance[i] = Math.min(distance[i], distance[i + size - 1] + Math.SQRT2);
    }
    return distance.map(value => { const t = Math.min(1, value / radius); return 1 - t * t * (3 - 2 * t); });
  }

  // MVT clockwise outer rings start polygons; following rings are land holes.
  function waterPolygons(geometries, extent) {
    const polygons = [];
    for (const rings of geometries) {
      let polygon;
      for (const ring of rings) {
        const area = ring.reduce((sum, p, i) => {
          const q = ring[(i + 1) % ring.length];
          return sum + p[0] * q[1] - q[0] * p[1];
        }, 0);
        if (area > 0) {
          polygon = { rings: [ring], enclosed: ring.every(([x, y]) => x > 0 && y > 0 && x < extent && y < extent) };
          polygons.push(polygon);
        } else if (polygon) polygon.rings.push(ring);
      }
    }
    return polygons;
  }

  function waterContains(rings, point) {
    const inside = ring => {
      let hit = false;
      for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
        const a = ring[i], b = ring[j];
        if ((a[1] > point[1]) !== (b[1] > point[1]) &&
            point[0] < (b[0] - a[0]) * (point[1] - a[1]) / (b[1] - a[1]) + a[0]) hit = !hit;
      }
      return hit;
    };
    return inside(rings[0]) && !rings.slice(1).some(inside);
  }

  function satelliteOceanSource(vectorSource, layerName, token, fetchTile = fetch) {
    // Standard basemap imports can include restricted internal tilesets.
    // The coastal mask uses the public Streets water geometry independently.
    const template = "https://api.mapbox.com/v4/mapbox.mapbox-streets-v8/{z}/{x}/{y}.vector.pbf";
    return {
      type: "custom", tileSize: 256, maxzoom: Math.min(16, vectorSource.data?.maxzoom ?? 16),
      async loadTile({ z, x, y }, { signal }) {
        const vectorUrl = new URL(template.replace("mapbox://tiles/", "https://api.mapbox.com/v4/").replace("{z}", z).replace("{x}", x).replace("{y}", y));
        if (!vectorUrl.searchParams.has("access_token")) vectorUrl.searchParams.set("access_token", token);
        const vector = await fetchTile(vectorUrl, { signal });
        if (!vector.ok) throw new Error("Ocean coastline tile unavailable");
        const { extent, geometries } = oceanTileGeometry(await vector.arrayBuffer(), layerName);
        if (!geometries.length) {
          const empty = document.createElement("canvas");
          empty.width = empty.height = 256;
          return empty;
        }
        const response = await fetchTile(`https://api.mapbox.com/v4/mapbox.satellite/${z}/${x}/${y}.webp?access_token=${encodeURIComponent(token)}`, { signal });
        if (!response.ok) throw new Error("Ocean imagery tile unavailable");
        const bitmap = await createImageBitmap(await response.blob());
        let mask, inlandCanvas;
        try {
          const canvas = document.createElement("canvas");
          canvas.width = canvas.height = 256;
          const context = canvas.getContext("2d");
          context.scale(256 / extent, 256 / extent);
          context.beginPath();
          for (const rings of geometries) for (const ring of rings) {
            ring.forEach(([px, py], index) => index ? context.lineTo(px, py) : context.moveTo(px, py));
            context.closePath();
          }
          // MVT winding preserves land holes, including islands within ocean tiles.
          context.clip("nonzero");
          context.drawImage(bitmap, 0, 0, extent, extent);
          const radius = Math.max(8, Math.min(384, 192 * Math.pow(2, z - 15)));
          const pad = Math.ceil(radius) + 2, size = 256 + pad * 2;
          mask = document.createElement("canvas");
          mask.width = mask.height = size;
          const water = mask.getContext("2d", { willReadFrequently: true });
          const parentZ = Math.max(0, z - 2), scale = Math.pow(2, z - parentZ);
          const jobs = [];
          const inlandParents = [];
          for (let py = Math.floor((y - pad / 256) / scale); py <= Math.floor((y + 1 + pad / 256) / scale); py++) {
            for (let px = Math.floor((x - pad / 256) / scale); px <= Math.floor((x + 1 + pad / 256) / scale); px++) {
              if (py < 0 || py >= Math.pow(2, parentZ)) continue;
              jobs.push((async () => {
                const wrappedX = ((px % Math.pow(2, parentZ)) + Math.pow(2, parentZ)) % Math.pow(2, parentZ);
                const url = new URL(template.replace("mapbox://tiles/", "https://api.mapbox.com/v4/").replace("{z}", parentZ).replace("{x}", wrappedX).replace("{y}", py));
                if (!url.searchParams.has("access_token")) url.searchParams.set("access_token", token);
                const response = await fetchTile(url, { signal });
                if (!response.ok) throw new Error("Ocean blend coastline unavailable");
                const geometry = oceanTileGeometry(await response.arrayBuffer(), layerName);
                // Parent geometry recognizes ponds that cross the output tile seam.
                for (const polygon of waterPolygons(geometry.geometries, geometry.extent)) {
                  if (!polygon.enclosed) continue;
                  inlandParents.push(polygon.rings.map(ring => ring.map(([vx, vy]) => [
                    (px * scale - x) * extent + vx * extent * scale / geometry.extent,
                    (py * scale - y) * extent + vy * extent * scale / geometry.extent,
                  ])));
                }
                water.fillStyle = "white";
                water.beginPath();
                for (const rings of geometry.geometries) for (const ring of rings) {
                  ring.forEach(([vx, vy], index) => {
                    const sx = pad + (px * scale - x) * 256 + vx * 256 * scale / geometry.extent;
                    const sy = pad + (py * scale - y) * 256 + vy * 256 * scale / geometry.extent;
                    if (index) water.lineTo(sx, sy); else water.moveTo(sx, sy);
                  });
                  water.closePath();
                }
                water.fill("nonzero");
              })());
            }
          }
          await Promise.all(jobs);
          signal.throwIfAborted();
          const alpha = oceanCoastalAlpha(water.getImageData(0, 0, size, size).data, size, radius);
          const pixels = context.getImageData(0, 0, 256, 256);
          for (let ty = 0; ty < 256; ty++) for (let tx = 0; tx < 256; tx++) {
            const i = (ty * 256 + tx) * 4, blend = alpha[(ty + pad) * size + tx + pad];
            pixels.data[i] = pixels.data[i] * blend + (8 * 1.265) * (1 - blend);
            pixels.data[i + 1] = pixels.data[i + 1] * blend + (16 * 1.265) * (1 - blend);
            pixels.data[i + 2] = pixels.data[i + 2] * blend + (27 * 1.265) * (1 - blend);
          }
          inlandCanvas = document.createElement("canvas");
          inlandCanvas.width = inlandCanvas.height = 256;
          const inland = inlandCanvas.getContext("2d", { willReadFrequently: true });
          inland.scale(256 / extent, 256 / extent);
          inland.fillStyle = "white";
          for (const polygon of waterPolygons(geometries, extent)) {
            if (!polygon.enclosed && !inlandParents.some(parent =>
              polygon.rings[0].some(point => waterContains(parent, point)))) continue;
            inland.beginPath();
            for (const ring of polygon.rings) {
              ring.forEach(([px, py], index) => index ? inland.lineTo(px, py) : inland.moveTo(px, py));
              inland.closePath();
            }
            inland.fill("nonzero");
          }
          const inlandPixels = inland.getImageData(0, 0, 256, 256).data;
          for (let i = 0; i < inlandPixels.length; i += 4) {
            if (!inlandPixels[i + 3]) continue;
            // Replace edge RGB too; retain original coverage to avoid a photo fringe.
            pixels.data[i] = 8;
            pixels.data[i + 1] = 16;
            pixels.data[i + 2] = 27;
          }
          context.putImageData(pixels, 0, 0);
          return canvas;
        } finally {
          bitmap.close();
          // Release large scratch backing stores, including on aborted tiles.
          for (const canvas of [mask, inlandCanvas]) {
            if (canvas) canvas.width = canvas.height = 0;
          }
        }
      },
    };
  }

  function threadedOceanSource(vectorSource, layerName, token, map) {
    const download = (url, options) => {
      const parsed = new URL(url);
      if (parsed.hostname === "api.mapbox.com" && parsed.pathname.startsWith("/v4/")) {
        // Use the active map session SKU, refreshed by the SDK for each request.
        url = map._requestManager.normalizeTileURL("mapbox://tiles/" + parsed.pathname.slice(4));
      }
      return fetch(url, options);
    };
    const source = satelliteOceanSource(vectorSource, layerName, token, download);
    if (typeof Worker === "undefined" || typeof OffscreenCanvas === "undefined") return source;
    const code = `
      const document = { createElement: () => new OffscreenCanvas(256, 256) };
      // Blob workers lack the storefront referrer required by URL-restricted tokens.
      // Download on the page; retain decoding and pixel processing in this worker.
      let requestId = 0;
      const downloads = new Map();
      const fetch = (url, { signal }) => new Promise((resolve, reject) => {
        const id = ++requestId;
        const cancel = () => { downloads.delete(id); postMessage({ cancelDownload: id }); reject(signal.reason); };
        if (signal.aborted) { reject(signal.reason); return; }
        downloads.set(id, { resolve, reject, cleanup: () => signal.removeEventListener("abort", cancel) });
        signal.addEventListener("abort", cancel, { once: true });
        postMessage({ download: id, url: String(url) });
      });
      ${oceanTileGeometry.toString()}
      ${oceanCoastalAlpha.toString()}
      ${waterPolygons.toString()}
      ${waterContains.toString()}
      ${satelliteOceanSource.toString()}
      let source;
      const jobs = new Map();
      onmessage = async ({ data }) => {
        if (data.downloadResult) {
          const task = downloads.get(data.downloadResult);
          if (!task) return;
          downloads.delete(data.downloadResult); task.cleanup();
          if (data.error) task.reject(new Error(data.error));
          else task.resolve(new Response(data.bytes, { status: data.status }));
          return;
        }
        if (data.init) { source = satelliteOceanSource(...data.init); return; }
        if (data.cancel) { jobs.get(data.id)?.abort(); return; }
        const controller = new AbortController();
        jobs.set(data.id, controller);
        let canvas;
        try {
          canvas = await source.loadTile(data.tile, { signal: controller.signal });
          controller.signal.throwIfAborted();
          const bitmap = canvas.transferToImageBitmap();
          postMessage({ id: data.id, bitmap }, [bitmap]);
        } catch (error) { postMessage({ id: data.id, error: error.message }); }
        finally {
          if (canvas) canvas.width = canvas.height = 0;
          jobs.delete(data.id);
        }
      };
    `;
    const url = URL.createObjectURL(new Blob([code], { type: "text/javascript" }));
    let worker;
    try { worker = new Worker(url); } catch { URL.revokeObjectURL(url); return source; }
    URL.revokeObjectURL(url);
    const pending = new Map();
    const downloads = new Map();
    let sequence = 0, stopped = false;
    worker.postMessage({ init: [{ url: vectorSource.url, data: { tiles: vectorSource.data?.tiles, maxzoom: vectorSource.data?.maxzoom } }, layerName, token] });
    worker.onmessage = async ({ data }) => {
      if (data.cancelDownload) { downloads.get(data.cancelDownload)?.abort(); return; }
      if (data.download) {
        const controller = new AbortController();
        downloads.set(data.download, controller);
        try {
          const response = await download(data.url, { signal: controller.signal });
          const bytes = await response.arrayBuffer();
          if (!stopped) worker.postMessage({ downloadResult: data.download, status: response.status, bytes }, [bytes]);
        } catch (error) {
          if (!stopped) worker.postMessage({ downloadResult: data.download, error: error.message });
        } finally { downloads.delete(data.download); }
        return;
      }
      const task = pending.get(data.id);
      if (!task) { data.bitmap?.close(); return; }
      pending.delete(data.id);
      task.cleanup();
      if (data.error) task.reject(new Error(data.error)); else task.resolve(data.bitmap);
    };
    const dispose = () => {
      stopped = true;
      worker.terminate();
      for (const controller of downloads.values()) controller.abort();
      downloads.clear();
      for (const task of pending.values()) { task.cleanup(); task.reject(new Error("Ocean worker stopped")); }
      pending.clear();
    };
    worker.onerror = dispose;
    map.once("remove", dispose);
    return { ...source, loadTile(tile, { signal }) {
      if (stopped) return source.loadTile(tile, { signal });
      return new Promise((resolve, reject) => {
        if (signal.aborted) { reject(signal.reason); return; }
        const id = ++sequence;
        const cancel = () => { pending.delete(id); worker.postMessage({ id, cancel: true }); reject(signal.reason); };
        pending.set(id, { resolve, reject, cleanup: () => signal.removeEventListener("abort", cancel) });
        signal.addEventListener("abort", cancel, { once: true });
        worker.postMessage({ id, tile });
      });
    } };
  }

  function limitTileWork(source, limit) {
    const queue = [];
    let active = 0;
    function drain() {
      while (active < limit && queue.length) {
        const task = queue.shift();
        task.signal.removeEventListener("abort", task.cancel);
        if (task.signal.aborted) { task.reject(task.signal.reason); continue; }
        active++;
        Promise.resolve().then(() => source.loadTile(task.tile, { signal: task.signal }))
          .then(task.resolve, task.reject).finally(() => { active--; drain(); });
      }
    }
    return { ...source, loadTile(tile, { signal }) {
      return new Promise((resolve, reject) => {
        if (signal.aborted) { reject(signal.reason); return; }
        const task = { tile, signal, resolve, reject, cancel: () => {
          const index = queue.indexOf(task);
          if (index !== -1) queue.splice(index, 1);
          reject(signal.reason);
        } };
        signal.addEventListener("abort", task.cancel, { once: true });
        queue.push(task);
        drain();
      });
    } };
  }

  function mount(host) {
    const abort = new AbortController();
    const signal = abort.signal;
    let destroyed = false;
    const status = host.querySelector("[data-map-status]");
    const message = host.dataset;
    const mapboxgl = window.mapboxgl;
    const mobile = matchMedia("(max-width: 999px)").matches;
    const showroom = [-156.439755, 20.686674];
    const formerTenant = [-156.43971875309944, 20.68665920140191];
    const opening = () => ({
      center: showroom,
      zoom: host.clientWidth < 600 ? 17.1 : 17.9,
      pitch: host.clientWidth < 600 ? 50 : 62,
      bearing: 90,
    });
    const map = new mapboxgl.Map({
      container: host.querySelector("[data-map-canvas]"),
      accessToken: host.dataset.token,
      style: host.dataset.style,
      center: formerTenant,
      zoom: 20,
      pitch: 0,
      bearing: 0,
      cooperativeGestures: true,
      trackResize: false,
      // Bound retained offscreen tiles while preserving visible map detail.
      ...(mobile ? {
        center: [-157.5, 20.5], zoom: 6, pitch: 0, bearing: -18,
        minTileCacheSize: 1, maxTileCacheSize: 16,
      } : {}),
    });

    map.once("style.load", () => {
      const style = map.getStyle();
      const imported = style.imports.find((item) => item.id === "basemap");
      const basemap = structuredClone(imported.data);
      basemap.schema.rrOpeningLabels = { type: "boolean", default: true };
      basemap.schema.rrOpeningRoads = { type: "boolean", default: true };
      basemap.schema.rrRoadOpacity = { type: "number", default: 1 };
      basemap.schema.rrBuildingOpacity = { type: "number", default: 1 };
      const fadeBuildingOpacity = (value, config = "rrBuildingOpacity") => {
        const multiply = (opacity) => ["*", opacity, ["config", config]];
        // Preserve let bindings around top-level camera expressions.
        if (Array.isArray(value) && value[0] === "let")
          return [...value.slice(0, -1), fadeBuildingOpacity(value[value.length - 1], config)];
        // Keep zoom at the top level, as required by camera paint expressions.
        if (Array.isArray(value) && value[0] === "interpolate" && value[2]?.[0] === "zoom")
          return value.map((part, index) => index >= 4 && index % 2 === 0 ? multiply(part) : part);
        if (Array.isArray(value) && value[0] === "step" && value[1]?.[0] === "zoom")
          return value.map((part, index) => index >= 2 && index % 2 === 0 ? multiply(part) : part);
        return multiply(value);
      };
      // Promote only the three nearby resort landmarks; retain native POI styling/state.
      const priorityResort = ["in", ["coalesce", ["get", "name_en"], ["get", "name"]],
        ["literal", ["Grand Wailea", "Four Seasons Resort", "Wailea Beach Marriott Resort & Spa"]]];
      for (const layer of basemap.layers) {
        if (layer.id === "poi-label") {
          layer.filter = ["all", layer.filter ?? true, ["!", priorityResort]];
        }
        // Recolor only vector sand; keep photographic surf and ocean untouched.
        if (["landuse", "recreation-polygon"].includes(layer.id) && layer.type === "fill") {
          layer.paint["fill-color"] = ["case",
            ["any", ["==", ["get", "class"], "sand"], ["==", ["get", "type"], "sand"]],
            "#000000", layer.paint["fill-color"]];
        }
        // Carry the opening ocean's near-black navy into the vector map.
        if (layer.id === "water" || layer.id === "waterway") {
          layer.paint ||= {};
          const type = layer.type;
          layer.paint[`${type}-color`] = layer.id === "water"
            ? ["case", ["==", ["get", "class"], "ocean"], "rgb(10.12, 20.24, 34.155)", "#08101b"]
            : "#08101b";
          layer.paint[`${type}-color-use-theme`] = "none";
          layer.paint[`${type}-emissive-strength`] = 1;
        }
        if (layer.id === "water-depth") {
          layer.layout ||= {};
          layer.layout.visibility = "none";
        }
        if (layer.type === "fill-extrusion" || layer.type === "building" ||
            layer.id === "building-models" || /^(building-underground|indoor-building-footprint|2d-building-outline)$/.test(layer.id)) {
          layer.paint ||= {};
          const opacity = `${layer.type}-opacity`;
          layer.paint[opacity] = fadeBuildingOpacity(layer.paint[opacity] ?? 1);
          layer.paint[`${opacity}-transition`] = { duration: 0 };
        }
        // Keep walking paths tied to the published road palette.
        if (/^(road|bridge|tunnel)-(path|pedestrian|steps)(-|$)/.test(layer.id) &&
            !layer.id.includes("cycleway-piste") &&
            (layer.type === "line" || layer.type === "fill")) {
          layer.paint ||= {};
          layer.paint[`${layer.type}-color`] = ["config", "colorRoads"];
        }
        const road =
          layer["source-layer"] === "road" ||
          layer["source-layer"]?.startsWith("hd_road") ||
          layer.id === "3d-bridges";
        if (road && ["line", "fill", "fill-extrusion", "model"].includes(layer.type)) {
          layer.paint ||= {};
          const opacity = `${layer.type}-opacity`;
          layer.paint[opacity] = fadeBuildingOpacity(layer.paint[opacity] ?? 1, "rrRoadOpacity");
          layer.paint[`${opacity}-transition`] = { duration: 0 };
        }
        const gate =
          layer.type === "symbol"
            ? "rrOpeningLabels"
            : road
              ? "rrOpeningRoads"
              : null;
        if (!gate) continue;
        layer.layout ||= {};
        layer.layout.visibility = [
          "case",
          ["config", gate],
          layer.layout.visibility ?? "visible",
          "none",
        ];
      }
      imported.data = basemap;
      imported.config = {
        ...imported.config,
        rrOpeningLabels: true,
        rrOpeningRoads: true,
        rrBuildingOpacity: 1,
        rrRoadOpacity: 1,
      };
      map.setStyle(style, { diff: false });
    });

    const orbitMotion = matchMedia("(prefers-reduced-motion: reduce)");
    let orbitStarted = false,
      orbitDisabled = orbitMotion.matches || host.dataset.rotation !== "true",
      orbitFrame = 0;
    const stopDefaultOrbit = () => {
      if (!introComplete) return;
      orbitDisabled = true;
      cancelAnimationFrame(orbitFrame);
    };
    for (const event of ["dragstart", "zoomstart", "rotatestart", "pitchstart", "click"]) {
      map.on(event, (event) => {
        if (event.originalEvent) stopDefaultOrbit();
      });
    }
    for (const type of ["pointerdown", "keydown"]) {
      map.getContainer().addEventListener(type, (event) => {
        const control = event.target.closest?.(".mapboxgl-ctrl-group button");
        const mapKey = type === "keydown" &&
          ["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight", "+", "-", "=", "Enter", " "].includes(event.key);
        if (introComplete && (control || mapKey)) stopDefaultOrbit();
      }, { capture: true, signal });
    }
    orbitMotion.addEventListener(
      "change",
      (event) => {
        if (event.matches) {
          stopDefaultOrbit();
          if (introStarted && !introComplete) {
            cancelAnimationFrame(introFrame);
            map.jumpTo(opening());
            finishIntro();
          }
        }
      },
      { signal },
    );
    function startDefaultOrbit() {
      if (orbitStarted || orbitDisabled) return;
      orbitStarted = true;
      let previous = performance.now();
      const turn = (now) => {
        if (orbitDisabled) return;
        const elapsed = Math.min(now - previous, 50);
        previous = now;
        if (!document.hidden && host.inView) {
          map.rotateTo(map.getBearing() + (1.995 * elapsed) / 1000, {
            around: showroom,
            duration: 0,
          });
        }
        orbitFrame = requestAnimationFrame(turn);
      };
      orbitFrame = requestAnimationFrame(turn);
    }
    map.on("remove", stopDefaultOrbit);
    let introStarted = false,
      introComplete = false,
      introFrame = 0;
    let openingFeaturesHidden = false;
    let openingRoadsVisible = true;
    function updateOpeningRoads() {
      if (!openingFeaturesHidden || introComplete) return;
      const canvas = map.getCanvas();
      const left = map.unproject([0, canvas.clientHeight / 2]);
      const right = map.unproject([
        canvas.clientWidth,
        canvas.clientHeight / 2,
      ]);
      const visible = left.distanceTo(right) <= 80467.2;
      if (visible === openingRoadsVisible) return;
      openingRoadsVisible = visible;
      map.setConfigProperty("basemap", "rrOpeningRoads", visible);
    }
    map.on("move", updateOpeningRoads);
    const mapElement = map.getContainer();
    mapElement.classList.remove("has-vignettes");
    mapElement.classList.add("cinematic");
    const introHandlers = [
      "scrollZoom",
      "boxZoom",
      "dragRotate",
      "dragPan",
      "keyboard",
      "doubleClickZoom",
      "touchZoomRotate",
      "touchPitch",
    ]
      .map((name) => map[name])
      .filter((handler) => handler && handler.isEnabled());
    introHandlers.forEach((handler) => handler.disable());
    const hawaiiCamera = () => {
      if (host.clientWidth < 700) {
        const width = mapElement.clientWidth;
        const height = mapElement.clientHeight;
        const angle = 18 * Math.PI / 180;
        const cos = Math.cos(angle);
        const sin = Math.sin(angle);
        // Fit the island coastline, not the empty corners of its geographic box.
        const points = [
          [-160.25, 21.9], [-160.05, 21.75],
          [-159.8, 22.23], [-159.3, 22.23],
          [-158.85, 22.16], [-157.4, 22.16],
          [-156.05, 21.49], [-155.1, 20.43],
          [-156.07, 20.27], [-154.81, 19.52],
          [-155.67, 18.91], [-156.05, 19.75],
        ].map(([lng, lat]) => {
          const x = (lng + 180) / 360 * 512;
          const y = (1 - Math.log(Math.tan(Math.PI / 4 + lat * Math.PI / 360)) / Math.PI) / 2 * 512;
          return [x * cos - y * sin, x * sin + y * cos];
        });
        const minX = Math.min(...points.map(([x]) => x));
        const maxX = Math.max(...points.map(([x]) => x));
        const minY = Math.min(...points.map(([, y]) => y));
        const maxY = Math.max(...points.map(([, y]) => y));
        const bottom = height * 0.72;
        const scale = Math.min(width * 0.84 / (maxX - minX),
          Math.max(100, bottom - height * 0.09) / (maxY - minY));
        const x = (minX + maxX) / 2;
        const y = maxY - (bottom - height / 2) / scale;
        const worldX = x * cos + y * sin;
        const worldY = -x * sin + y * cos;
        return {
          center: [worldX / 512 * 360 - 180,
            Math.atan(Math.sinh(Math.PI * (1 - 2 * worldY / 512))) * 180 / Math.PI],
          zoom: Math.log2(scale) + Math.log2(0.95), pitch: 0, bearing: -18,
        };
      }
      const welcomeSpace = host.clientWidth < 700
        ? (host.querySelector("[data-map-welcome]")?.offsetHeight || 200) + 48
        : 0;
      const fit = map.cameraForBounds(
        [
          [-160.25, 18.9],
          [-154.8, 22.25],
        ],
        {
          padding: {
            top: Math.round(mapElement.clientHeight * 0.08),
            bottom: host.clientWidth < 700
              ? Math.min(mapElement.clientHeight * 0.65, (host.querySelector("[data-map-welcome]")?.offsetHeight || 200) + 48)
              : Math.round(mapElement.clientHeight * 0.08),
            left: Math.round(mapElement.clientWidth * 0.08),
            right: Math.round(mapElement.clientWidth * 0.08),
          },
          pitch: 0,
          bearing: 0,
        },
      );
      return {
        center: [fit.center.lng, fit.center.lat -
          welcomeSpace / 2 * 360 / (512 * Math.pow(2, fit.zoom)) *
          Math.cos(fit.center.lat * Math.PI / 180)],
        zoom: fit.zoom + Math.log2(0.95),
        pitch: 0,
        bearing: 0,
      };
    };
    const waileaMarker = document.createElement("div");
    waileaMarker.className = "rr-showroom-map__wailea";
    const waileaName = document.createElement("span");
    waileaName.textContent = "Wailea";
    waileaMarker.append(waileaName);
    mapElement.parentElement.append(waileaMarker);
    function updateWaileaMarker() {
      const point = map.project(showroom);
      waileaMarker.style.transform = `translate(${point.x}px, ${point.y}px) rotate(calc(${-map.getBearing()}deg + var(--wailea-rotation-offset, 0deg)))`;
    }
    map.on("move", updateWaileaMarker);
    const islandLabel = host.querySelector("[data-island-label]");
    const islandCurve = host.querySelector("[data-island-curve]");
    const islandText = host.querySelector("[data-island-text]");
    let islandOpeningZoom = null;
    const islandCurveCoordinates = [
      [-158.8505182247, 22.0812107513],
      [-158.3783023572, 22.1541218650],
      [-157.4048749111, 22.1004015416],
      [-156.7421157988, 21.7930369505],
      [-156.0420764865, 21.4850116774],
      [-155.5367226634, 21.0874683482],
      [-155.1059292404, 20.4251221461],
    ];
    // Clip the opening halo to the actual water polygons, including coastline holes.
    let oceanMaskFrame = 0, previousOceanMask = "";
    function updateOceanMask() {
      oceanMaskFrame = 0;
      if (destroyed || introComplete || (introStarted && map.getZoom() >= 12) || !map.getLayer("showroom-water")) return;
      const features = map.queryRenderedFeatures({ layers: ["showroom-water"] });
      const paths = [];
      for (const { geometry } of features) {
        const polygons = geometry.type === "Polygon" ? [geometry.coordinates]
          : geometry.type === "MultiPolygon" ? geometry.coordinates : [];
        for (const polygon of polygons) {
          const d = polygon.map(ring => ring.map((coordinate, i) => {
            const point = map.project(coordinate);
            return `${i ? "L" : "M"}${point.x.toFixed(2)},${point.y.toFixed(2)}`;
          }).join(" ") + "Z").join(" ");
          paths.push(`<path d="${d}" fill="white" fill-rule="evenodd"/>`);
        }
      }
      // Keep a soft ocean margin around the two end islands at every viewport size.
      const clearings = [
        [[-159.82, 21.86], [-159.28, 22.25]], // Kauai
        [[-156.08, 18.89], [-154.80, 20.29]], // Hawaii / Big Island
      ].map(([sw, ne]) => {
        const corners = [sw, ne, [sw[0], ne[1]], [ne[0], sw[1]]].map(point => map.project(point));
        const xs = corners.map(point => point.x), ys = corners.map(point => point.y);
        const left = Math.min(...xs), right = Math.max(...xs);
        const top = Math.min(...ys), bottom = Math.max(...ys);
        const margin = Math.max(22, Math.min(mapElement.clientWidth, mapElement.clientHeight) * 0.045);
        // Inner clear ellipse encloses the complete island bounds plus its blue coast.
        const rx = ((right - left) * 0.71 + margin) / 0.72;
        const ry = ((bottom - top) * 0.71 + margin) / 0.72;
        return `<ellipse cx="${(left + right) / 2}" cy="${(top + bottom) / 2}" rx="${rx}" ry="${ry}" fill="url(#coastal-clear)"/>`;
      }).join("");
      const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${mapElement.clientWidth} ${mapElement.clientHeight}"><defs><radialGradient id="coastal-clear"><stop offset="72%" stop-color="black"/><stop offset="100%" stop-color="white"/></radialGradient><mask id="ocean-halo" maskUnits="userSpaceOnUse" x="0" y="0" width="${mapElement.clientWidth}" height="${mapElement.clientHeight}" style="mask-type:luminance"><rect width="100%" height="100%" fill="white"/>${clearings}</mask></defs><g mask="url(#ocean-halo)">${paths.join("")}</g></svg>`;
      if (svg !== previousOceanMask) {
        previousOceanMask = svg;
        mapElement.style.setProperty("--rr-ocean-mask", `url("data:image/svg+xml,${encodeURIComponent(svg)}")`);
      }
    }
    function scheduleOceanMask() {
      if (!introComplete && (!introStarted || map.getZoom() < 12) && !oceanMaskFrame) oceanMaskFrame = requestAnimationFrame(updateOceanMask);
    }
    map.on("move", scheduleOceanMask);
    map.on("idle", scheduleOceanMask);
    function updateIslandLabel() {
      if (!islandLabel || !mapElement.classList.contains("ready")) return;
      updateWaileaMarker();
      if (!introStarted) islandOpeningZoom = map.getZoom();
      const fade = introComplete
        ? 1
        : Math.max(0, Math.min(1, (map.getZoom() - islandOpeningZoom) / 1.5));
      islandLabel.style.opacity = String(1 - fade * fade * (3 - 2 * fade));
      if (fade === 1) return;
      const points = islandCurveCoordinates.map((coordinate) => {
        const point = map.project(coordinate);
        return `${point.x},${point.y}`;
      });
      islandLabel.setAttribute(
        "viewBox",
        `0 0 ${mapElement.clientWidth} ${mapElement.clientHeight}`,
      );
      islandCurve.setAttribute(
        "d",
        `M ${points[0]} C ${points.slice(1, 4).join(" ")} C ${points.slice(4, 7).join(" ")}`,
      );
      const length = islandCurve.getTotalLength();
      islandText.setAttribute("textLength", String(length * 0.94));
      islandText.setAttribute(
        "font-size",
        String(Math.max(10, Math.min(36, (28.597163 * length) / 1059))),
      );
    }
    map.on("move", updateIslandLabel);
    function openingBlend(zoom) {
      const smooth = (value) => {
        const t = Math.max(0, Math.min(1, value));
        return t * t * (3 - 2 * t);
      };
      return {
        // Complete the basemap handoff before close satellite tiles are needed.
        basemap: smooth((zoom - 10) / 2),
        // Buildings first load at zoom 15; start transparent at that threshold.
        buildings: smooth((zoom - 15) / 1.8),
      };
    }
    function updateOpeningBlend() {
      const blend = openingBlend(map.getZoom());
      const previous = updateOpeningBlend.previous || {};
      if (previous.basemap !== blend.basemap) {
        // Preserve opening ocean color until the zoom-driven imagery handoff.
        mapElement.style.setProperty("--rr-ocean-opacity", String(1 - blend.basemap));
        mapElement.style.setProperty("--rr-bottom-fade-start", `${92 - 14 * blend.basemap}%`);
        map.setPaintProperty("showroom-opening-satellite", "raster-opacity", 1 - blend.basemap);
        map.setPaintProperty("showroom-satellite-ocean", "raster-opacity", blend.basemap);
        map.setPaintProperty("showroom-water", "fill-opacity", 1 - blend.basemap);
        map.setConfigProperty("basemap", "rrRoadOpacity", blend.basemap);
      }
      if (previous.buildings !== blend.buildings) map.setConfigProperty("basemap", "rrBuildingOpacity", blend.buildings);
      updateOpeningBlend.previous = blend;
    }
    function finishIntro() {
      introComplete = true;
      mapElement.style.setProperty("--rr-ocean-opacity", "0");
      mapElement.style.setProperty("--rr-bottom-fade-start", "78%");
      map.setPaintProperty("showroom-satellite-ocean", "raster-opacity", 1);
      updateIslandLabel();
      map.setLayoutProperty("showroom-opening-satellite", "visibility", "none");
      map.setLayoutProperty("showroom-water", "visibility", "none");
      if (mobile) {
        for (const id of ["showroom-opening-satellite", "showroom-water"]) {
          map.removeLayer(id);
          map.removeSource(id);
        }
        mapElement.style.removeProperty("--rr-ocean-mask");
      }
      map.setConfigProperty("basemap", "rrOpeningLabels", true);
      map.setConfigProperty("basemap", "rrOpeningRoads", true);
      map.setConfigProperty("basemap", "rrRoadOpacity", 1);
      map.setConfigProperty("basemap", "rrBuildingOpacity", 1);
      map.setLayoutProperty("wailea-mall-logo", "visibility", "visible");
      map.setLayoutProperty("wailea-resort-logos", "visibility", "visible");
      map.setPaintProperty("showroom-pin", "icon-opacity", 1);
      mapElement.classList.remove("cinematic");
      introHandlers.forEach((handler) => handler.enable());
      status.textContent = "";
      startDefaultOrbit();
    }
    let scrollFrame = 0, centering = false;
    const startButton = host.querySelector("[data-map-start]");
    const welcome = host.querySelector("[data-map-welcome]");
    welcome.classList.remove("is-leaving");
    welcome.inert = false;
    startButton.disabled = true;
    startButton.hidden = false;
    startButton.addEventListener(
      "click",
      () => {
        if (!mapElement.classList.contains("ready") || introStarted || centering) return;
        welcome.classList.add("is-leaving");
        waileaMarker.classList.add("is-leaving");
        welcome.inert = true;
        startButton.blur();
        map.setLayoutProperty("showroom-satellite-ocean", "visibility", "visible");
        centering = true;
        const frame = host.querySelector(".rr-showroom-map__frame");
        const rect = frame.getBoundingClientRect();
        const from = window.scrollY;
        const target = Math.max(0, Math.min(
          from + rect.top + (rect.height - window.innerHeight) / 2,
          document.documentElement.scrollHeight - window.innerHeight,
        ));
        const startedAt = performance.now();
        const duration = orbitMotion.matches ? 0 : 650;
        const center = now => {
          if (destroyed) return;
          const progress = duration ? Math.min((now - startedAt) / duration, 1) : 1;
          const eased = progress * progress * (3 - 2 * progress);
          window.scrollTo({ top: from + (target - from) * eased, behavior: "instant" });
          if (progress < 1) {
            scrollFrame = requestAnimationFrame(center);
            return;
          }
          scrollFrame = 0;
          centering = false;
          startCinematic();
        };
        scrollFrame = requestAnimationFrame(center);
      },
      { signal },
    );
    function startCinematic() {
      if (introStarted) return;
      introStarted = true;
      mapElement.classList.add("has-vignettes");
      const finish = finishIntro;
      const destination = { ...opening(), center: showroom, bearing: 90 };
      if (orbitMotion.matches || host.dataset.intro !== "true") {
        map.jumpTo(destination);
        finish();
        return;
      }
      const origin = map.getCenter(),
        initialZoom = map.getZoom(),
        initialBearing = map.getBearing();
      let elapsed = 0,
        previous = performance.now();
      const frame = (now) => {
        const delta = now - previous;
        previous = now;
        if (document.hidden || !host.inView) {
          introFrame = requestAnimationFrame(frame);
          return;
        }
        elapsed += Math.min(delta, 100);
        const t = Math.min(elapsed / 8000, 1);
        const ease = t * t * (3 - 2 * t);

        const remaining = Math.pow(1 - ease, 5);

        const turnProgress = Math.max(0, Math.min(1, (t - 0.22) / 0.5));
        const oceanTurn = turnProgress * turnProgress * (3 - 2 * turnProgress);
        map.jumpTo({
          center: [
            showroom[0] + (origin.lng - showroom[0]) * remaining,
            showroom[1] + (origin.lat - showroom[1]) * remaining,
          ],
          zoom: initialZoom + (destination.zoom - initialZoom) * ease,
          pitch: destination.pitch * ease,
          bearing: initialBearing + (destination.bearing - initialBearing) * oceanTurn,
        });
        updateOpeningBlend();
        const fade = Math.max(
          0,
          Math.min(1, (map.getZoom() - (destination.zoom - 2)) / 1.6),
        );
        map.setPaintProperty(
          "showroom-pin",
          "icon-opacity",
          fade * fade * (3 - 2 * fade),
        );
        if (t < 1) introFrame = requestAnimationFrame(frame);
        else finish();
      };
      introFrame = requestAnimationFrame(frame);
    }
    map.on("remove", () => {
      cancelAnimationFrame(introFrame);
      cancelAnimationFrame(orbitFrame);
    });
    map.addControl(new mapboxgl.NavigationControl(), "top-right");
    const compassButton = map.getContainer().querySelector(".mapboxgl-ctrl-compass");
    compassButton.addEventListener("click", event => {
      event.preventDefault();
      event.stopImmediatePropagation();
      stopDefaultOrbit();
      map.easeTo({ bearing: 0, pitch: 0, duration: orbitMotion.matches ? 0 : 700 });
    }, { capture: true, signal });
    // Zoom clicks and held zoom/rotation run 66% faster than the original controls.
    const controlSpeedMultiplier = 1.66;
    let zoomHold = null, zoomHoldTimer = 0, zoomHoldFrame = 0;
    const stopZoomHold = () => {
      clearTimeout(zoomHoldTimer);
      cancelAnimationFrame(zoomHoldFrame);
      zoomHold = null;
    };
    for (const [selector, direction] of [[".mapboxgl-ctrl-zoom-in", 1], [".mapboxgl-ctrl-zoom-out", -1]]) {
      const button = map.getContainer().querySelector(selector);
      let held = false;
      button.style.touchAction = "none";
      button.style.userSelect = "none";
      const start = () => {
        stopZoomHold();
        held = false;
        stopDefaultOrbit();
        map.stop();
        zoomHold = button;
        zoomHoldTimer = setTimeout(() => {
          if (zoomHold !== button) return;
          held = true;
          let previous = performance.now();
          const tick = now => {
            if (zoomHold !== button) return;
            const elapsed = Math.min(now - previous, 50);
            previous = now;
            const zoom = Math.max(map.getMinZoom(), Math.min(map.getMaxZoom(), map.getZoom() + direction * controlSpeedMultiplier * elapsed / 1000));
            map.setZoom(zoom);
            zoomHoldFrame = requestAnimationFrame(tick);
          };
          zoomHoldFrame = requestAnimationFrame(tick);
        }, 200);
      };
      button.addEventListener("pointerdown", event => {
        if (event.button !== 0 || button.disabled) return;
        button.setPointerCapture(event.pointerId);
        start();
      }, { signal });
      for (const type of ["pointerup", "pointercancel", "lostpointercapture", "blur"])
        button.addEventListener(type, () => {
          // Focus can leave the previous button after the next press has started.
          if (zoomHold === button) stopZoomHold();
        }, { signal });
      button.addEventListener("keydown", event => {
        if (event.key !== " " && event.key !== "Enter") return;
        event.preventDefault();
        if (!event.repeat) start();
      }, { signal });
      button.addEventListener("keyup", event => {
        if (event.key !== " " && event.key !== "Enter") return;
        event.preventDefault();
        stopZoomHold();
        button.click();
      }, { signal });
      button.addEventListener("click", event => {
        event.preventDefault();
        event.stopImmediatePropagation();
        if (!held && !button.disabled) {
          stopDefaultOrbit();
          map.zoomTo(map.getZoom() + direction * controlSpeedMultiplier, {
            duration: orbitMotion.matches ? 0 : 300,
          });
        }
        held = false;
      }, { capture: true, signal });
      button.addEventListener("contextmenu", event => event.preventDefault(), { signal });
    }
    window.addEventListener("blur", stopZoomHold, { signal });
    document.addEventListener("visibilitychange", () => {
      if (document.hidden) stopZoomHold();
    }, { signal });
    map.on("remove", stopZoomHold);
    class RotationControl {
      onAdd(map) {
        this.container = document.createElement("div");
        this.container.className = "mapboxgl-ctrl mapboxgl-ctrl-group";
        this.cleanup = new AbortController();
        const listen = (target, event, handler) =>
          target.addEventListener(event, handler, {
            signal: this.cleanup.signal,
          });
        let active = null,
          timer = 0,
          frame = 0;
        this.stop = () => {
          clearTimeout(timer);
          cancelAnimationFrame(frame);
          if (active) {
            active.button.dataset.held = String(active.held);
            active = null;
          }
        };
        const start = (button, angle) => {
          this.stop();
          button.dataset.held = "false";
          map.stop();
          active = { button, held: false };
          timer = setTimeout(() => {
            if (!active) return;
            active.held = true;
            let previous = performance.now();
            const turn = (now) => {
              if (!active) return;
              const elapsed = Math.min(now - previous, 50);
              previous = now;
              map.setBearing(
                map.getBearing() + (Math.sign(angle) * 60 * controlSpeedMultiplier * elapsed) / 1000,
              );
              frame = requestAnimationFrame(turn);
            };
            frame = requestAnimationFrame(turn);
          }, 200);
        };
        for (const [label, angle] of [
          [message.rotateLeft, -30],
          [message.rotateRight, 30],
        ]) {
          const button = document.createElement("button");
          button.type = "button";
          button.title = label;
          button.setAttribute("aria-label", label);
          button.style.touchAction = "none";
          button.style.userSelect = "none";
          button.innerHTML =
            '<svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" style="display:block;margin:auto;' +
            (angle < 0 ? "transform:scaleX(-1);" : "") +
            '"><path d="M19 10a7 7 0 1 0-1.2 6.5" fill="none" stroke="#333" stroke-width="2.5" stroke-linecap="round"/><path d="M14 10h6V4" fill="none" stroke="#333" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
          listen(button, "pointerdown", (event) => {
            if (event.button !== 0) return;
            button.setPointerCapture(event.pointerId);
            start(button, angle);
          });
          for (const event of [
            "pointerup",
            "pointercancel",
            "lostpointercapture",
            "blur",
          ])
            listen(button, event, () => {
              if (active?.button === button) this.stop();
            });
          listen(button, "keydown", (event) => {
            if (event.key !== " " && event.key !== "Enter") return;
            event.preventDefault();
            if (!event.repeat) start(button, angle);
          });
          listen(button, "keyup", (event) => {
            if (event.key !== " " && event.key !== "Enter") return;
            event.preventDefault();
            this.stop();
            button.click();
          });
          listen(button, "click", () => {
            if (button.dataset.held !== "true")
              map.rotateTo(map.getBearing() + angle, { duration: 300 });
            button.dataset.held = "false";
          });
          listen(button, "contextmenu", (event) => event.preventDefault());
          this.container.appendChild(button);
        }
        listen(window, "blur", this.stop);
        listen(document, "visibilitychange", () => {
          if (document.hidden) this.stop();
        });
        return this.container;
      }
      onRemove() {
        this.stop();
        this.cleanup.abort();
        this.container.remove();
      }
    }
    map.addControl(new RotationControl(), "top-right");

    class RecenterControl {
      onAdd(map) {
        this.map = map;
        this.container = document.createElement("div");
        this.container.className = "mapboxgl-ctrl showroom-recenter-control";
        this.container.inert = true;
        this.container.setAttribute("aria-hidden", "true");
        const visits = document.createElement("div");
        visits.className = "showroom-actions__group showroom-actions__visits";
        host.querySelectorAll(".rr-showroom-map__details nav a").forEach((link) => {
          const action = link.cloneNode(true);
          action.className = "button button--outline showroom-action";
          visits.appendChild(action);
        });
        const returnGroup = document.createElement("div");
        returnGroup.className = "showroom-actions__group showroom-actions__return";
        const button = document.createElement("button");
        button.className = "button button--outline showroom-action showroom-recenter";
        button.type = "button";
        button.textContent = message.recenter;
        button.onclick = () => {
          cancelAnimationFrame(orbitFrame);
          map.stop();
          orbitStarted = false;
          orbitDisabled =
            orbitMotion.matches || host.dataset.rotation !== "true";
          this.returning = true;
          this.updateVisibility();
          this.finishReturn = () => {
            this.returning = false;
            this.updateVisibility();
            if (!orbitDisabled) startDefaultOrbit();
          };
          map.once("moveend", this.finishReturn);
          map.flyTo({
            ...opening(),
            center: showroom,
            duration: orbitMotion.matches ? 0 : 1200,
          });
        };
        returnGroup.appendChild(button);
        this.container.append(visits, returnGroup);
        this.updateVisibility = () => {
          if (
            !map.getLayer("showroom-pin") ||
            !mapElement.classList.contains("ready")
          )
            return;
          const canvas = map.getCanvas();
          const visible =
            map.queryRenderedFeatures(
              [
                [0, 0],
                [canvas.clientWidth, canvas.clientHeight],
              ],
              { layers: ["showroom-pin"] },
            ).length > 0;

          const left = map.unproject([0, canvas.clientHeight / 2]);
          const right = map.unproject([
            canvas.clientWidth,
            canvas.clientHeight / 2,
          ]);
          const widerThanTwoMiles = left.distanceTo(right) > 3218.688;
          const show = introComplete && !this.returning && (!visible || widerThanTwoMiles);
          const state = introComplete ? (show ? "return" : "visits") : "hidden";
          if (state === this.state) return;
          this.state = state;
          this.container.classList.toggle("is-visible", introComplete);
          this.container.inert = !introComplete;
          this.container.setAttribute("aria-hidden", String(!introComplete));
          const outgoing = show ? visits : returnGroup;
          const transferFocus = outgoing.contains(document.activeElement);
          for (const [group, active] of [[visits, state === "visits"], [returnGroup, state === "return"]]) {
            group.classList.toggle("is-active", active);
            group.inert = !active;
            group.setAttribute("aria-hidden", String(!active));
          }
          if (transferFocus) {
            const next = show ? button : visits.querySelector("a");
            next?.focus({ preventScroll: true });
          }
        };
        // Opacity-hidden POIs remain queryable, so they can reappear when clear.
        // Own only the states we changed; preserve permanent tenant/mall hiding.
        this.coveredPOIs = new Map();
        this.updateCoveredPOIs = () => {
          if (!introComplete || !map.getLayer("showroom-pin")) return;
          const canvas = map.getCanvas().getBoundingClientRect();
          const covered = new Map();
          for (const group of [visits, returnGroup]) {
            if (getComputedStyle(group).visibility === "hidden") continue;
            for (const action of group.children) {
              const bounds = action.getBoundingClientRect();
              const queryBounds = [
                [bounds.left - canvas.left - 8, bounds.top - canvas.top - 8],
                [bounds.right - canvas.left + 8, bounds.bottom - canvas.top + 8],
              ];
              const features = map.queryRenderedFeatures(queryBounds, { target: { featuresetId: "poi", importId: "basemap" } });
              if (map.getLayer("wailea-resort-logos")) features.push(...map.queryRenderedFeatures(queryBounds, { layers: ["wailea-resort-logos"] }));
              for (const feature of features) {
                if (feature.id == null) continue;
                const key = JSON.stringify([feature.id, feature.namespace, feature.source]);
                if (this.coveredPOIs.has(key)) covered.set(key, feature);
                else if (!map.getFeatureState(feature).hide) {
                  map.setFeatureState(feature, { hide: true });
                  covered.set(key, feature);
                }
              }
            }
          }
          for (const [key, feature] of this.coveredPOIs) {
            if (!covered.has(key)) map.setFeatureState(feature, { hide: false });
          }
          this.coveredPOIs = covered;
        };
        this.onActionTransitionEnd = () => map.triggerRepaint();
        this.container.addEventListener("transitionend", this.onActionTransitionEnd);
        map.on("render", this.updateVisibility);
        map.on("render", this.updateCoveredPOIs);
        return this.container;
      }
      onRemove() {
        this.map.off("render", this.updateVisibility);
        this.map.off("render", this.updateCoveredPOIs);
        this.container.removeEventListener("transitionend", this.onActionTransitionEnd);
        for (const feature of this.coveredPOIs.values()) {
          this.map.setFeatureState(feature, { hide: false });
        }
        this.coveredPOIs.clear();
        if (this.finishReturn) this.map.off("moveend", this.finishReturn);
        this.container.remove();
      }
    }
    map.addControl(new RecenterControl(), "top-left");
    map.on("load", async () => {
      try {
        const basemap = map
          .getStyle()
          .imports.find((item) => item.id === "basemap").data;
        const water = basemap.layers.find((layer) => layer.id === "water");
        const oceanSource = threadedOceanSource(basemap.sources[water.source], water["source-layer"], host.dataset.token, map);
        map.addSource("showroom-satellite-ocean", mobile ? limitTileWork(oceanSource, 1) : oceanSource);
        map.addLayer({
          id: "showroom-satellite-ocean", type: "raster", source: "showroom-satellite-ocean",
          slot: "middle", layout: { visibility: "none" },
          paint: { "raster-opacity": 0, "raster-opacity-transition": { duration: 0 }, "raster-emissive-strength": 1, "raster-fade-duration": 400 },
        });
        map.addSource("showroom-opening-satellite", {
          type: "raster",
          url: "mapbox://mapbox.satellite",
          // Sample one closer imagery level to avoid the coarse Big Island mosaic seam.
          tileSize: 128,
        });
        map.addLayer({
          id: "showroom-opening-satellite",
          type: "raster",
          source: "showroom-opening-satellite",
          // Reveal coastal blue above the retained black water base.
          slot: "middle",
          layout: { visibility: "none" },
          paint: {
            "raster-emissive-strength": 1,
            "raster-fade-duration": 400,
            "raster-opacity-transition": { duration: 0 },
          },
        });
        map.addSource("showroom-water", basemap.sources[water.source]);
        map.addLayer({
          id: "showroom-water",
          type: "fill",
          source: "showroom-water",
          "source-layer": water["source-layer"],
          slot: "bottom",
          paint: {
            "fill-opacity-transition": { duration: 0 },
            "fill-color": "#000000",
            "fill-color-use-theme": "none",
            "fill-emissive-strength": 1,
            "fill-antialias": false,
          },
        });
        const hiddenFormerTenants = new Set();
        let openingRestored = false;
        let arrivalPreloaded = false;
        let mallLabelHidden = false;
        function hideFormerTenant() {
          let changed = false;
          for (const feature of map.queryRenderedFeatures({
            target: { featuresetId: "poi", importId: "basemap" },
          })) {
            if (
              feature.properties.name !== "Rolex" ||
              feature.geometry.type !== "Point"
            )
              continue;
            const [lng, lat] = feature.geometry.coordinates;
            if (
              Math.abs(lng - formerTenant[0]) > 0.0002 ||
              Math.abs(lat - formerTenant[1]) > 0.0002
            )
              continue;
            const key = JSON.stringify([feature.id, feature.namespace, feature.source]);
            if (hiddenFormerTenants.has(key)) continue;
            map.setFeatureState(feature, { hide: true });
            hiddenFormerTenants.add(key);
            changed = true;
          }
          return changed;
        }
        map.on("idle", () => {
          if (!map.getLayer("showroom-pin")) return;
          // Set POI state only after the custom image and symbol have rendered.
          // Mapbox 3.30 can fail when image updates overlap feature-state changes.
          if (!mallLabelHidden) {
            // Published mall POI, inspected at its original label position.
            map.setFeatureState(
              {
                target: { featuresetId: "poi", importId: "basemap" },
                id: 4496345731,
                namespace: "poi-A",
              },
              { hide: true },
            );
            mallLabelHidden = true;
            return;
          }
          if (hideFormerTenant()) return;
          if (!openingRestored) {
            openingRestored = true;
            openingFeaturesHidden = true;
            map.setConfigProperty("basemap", "rrOpeningLabels", false);
            map.jumpTo(hawaiiCamera());
            map.setLayoutProperty(
              "showroom-opening-satellite",
              "visibility",
              "visible",
            );
            updateOpeningRoads();
            updateOpeningBlend();
            return;
          }
          mapElement.classList.add("ready");
          host.classList.add("is-ready");
          updateIslandLabel();
          clearTimeout(loadTimeout);
          status.textContent = "";
          startButton.disabled = false;
          // Reveal the loaded opening first; route preloading must not gate readiness.
          if (!mobile && !arrivalPreloaded) {
            arrivalPreloaded = true;
            map.flyTo({ ...opening(), duration: 8000, preloadOnly: true });
          }
        });

        const logo = new Image();
        logo.crossOrigin = "anonymous";
        logo.src = host.dataset.logo;
        const mallLogo = new Image();
        mallLogo.crossOrigin = "anonymous";
        mallLogo.src = host.dataset.mallLogo;
        await Promise.all([logo.decode(), mallLogo.decode()]);
        if (destroyed) return;
        const canvas = document.createElement("canvas");
        canvas.width = 588;
        canvas.height = 210;
        const ctx = canvas.getContext("2d");
        ctx.scale(3, 3);
        ctx.save();
        ctx.translate(82, 0);
        ctx.beginPath();
        ctx.moveTo(16, 40);
        ctx.bezierCurveTo(12, 34, 3, 23, 3, 15);
        ctx.arc(16, 15, 13, Math.PI, 0);
        ctx.bezierCurveTo(29, 23, 20, 34, 16, 40);
        ctx.closePath();
        ctx.fillStyle = "#ffffff";
        ctx.fill();
        ctx.strokeStyle = "#394353";
        ctx.lineWidth = 1.5;
        ctx.stroke();
        ctx.beginPath();
        ctx.arc(16, 15, 4, 0, Math.PI * 2);
        ctx.fillStyle = "#394353";
        ctx.fill();
        ctx.restore();
        ctx.shadowColor = "#000";
        ctx.shadowBlur = 3;
        ctx.drawImage(logo, 17, 54, 162, (162 * 143) / 1765);
        map.addImage("showroom-pin", ctx.getImageData(0, 0, 588, 210), {
          pixelRatio: 3,
        });
        map.addSource("showroom-point", {
          type: "geojson",
          data: {
            type: "Feature",
            properties: { name: "Raiman Rocks" },
            geometry: { type: "Point", coordinates: showroom },
          },
        });

        const mallCanvas = document.createElement("canvas");
        mallCanvas.width = 426;
        mallCanvas.height = 204;
        const mallContext = mallCanvas.getContext("2d");
        mallContext.scale(3, 3);
        mallContext.drawImage(mallLogo, 6, 6, 130, (130 * 201) / 473);
        const mallStyle = mallPOIStyle(map);
        if (mallStyle.colorTheme) map.setColorTheme(mallStyle.colorTheme);
        map.addImage(
          "wailea-mall-logo",
          signedDistanceImage(mallContext.getImageData(0, 0, 426, 204)),
          { pixelRatio: 3, sdf: true },
        );
        map.addSource("wailea-mall", {
          type: "geojson",
          data: {
            type: "Feature",
            properties: { name: "The Shops at Wailea", class: "store_like" },
            geometry: {
              type: "Point",
              coordinates: [-156.44033163785934, 20.687161055991837],
            },
          },
        });
        map.addLayer({
          id: "wailea-mall-logo",
          type: "symbol",
          source: "wailea-mall",
          slot: "top",
          minzoom: 14,
          layout: {
            visibility: "none",
            "icon-image": "wailea-mall-logo",
            "icon-pitch-alignment": "viewport",
            "icon-rotation-alignment": "viewport",
            "symbol-z-elevate": true,
            "icon-allow-overlap": false,
            "icon-ignore-placement": false,
            "icon-size": [
              "interpolate",
              ["linear"],
              ["zoom"],
              14,
              0.67,
              17.9,
              1,
            ],
          },
          paint: {
            ...mallStyle.paint,
            "icon-opacity": ["interpolate", ["linear"], ["zoom"], 14, 0, 15, 1],
          },
        });

        const resorts = [
          { key: "grand-wailea", url: host.dataset.grandWaileaLogo, coordinates: [-156.4412435889244, 20.683456075377975] },
          { key: "four-seasons", url: host.dataset.fourSeasonsLogo, coordinates: [-156.4420348405838, 20.68030432408969] },
          { key: "wailea-beach", url: host.dataset.waileaBeachLogo, coordinates: [-156.4431157708168, 20.687312866678297] },
        ];
        for (const resort of resorts) {
          const image = new Image();
          image.crossOrigin = "anonymous";
          image.src = resort.url;
          await image.decode();
          if (destroyed) return;
          const height = 130 * image.naturalHeight / image.naturalWidth;
          const raster = document.createElement("canvas");
          raster.width = 426;
          raster.height = Math.ceil((height + 12) * 3);
          const context = raster.getContext("2d");
          context.scale(3, 3);
          context.drawImage(image, 6, 6, 130, height);
          map.addImage(resort.key, signedDistanceImage(context.getImageData(0, 0, raster.width, raster.height)), { pixelRatio: 3, sdf: true });
        }
        map.addSource("wailea-resorts", {
          type: "geojson",
          data: { type: "FeatureCollection", features: resorts.map((resort, id) => ({
            type: "Feature", id, properties: { image: resort.key, class: "lodging" },
            geometry: { type: "Point", coordinates: resort.coordinates },
          })) },
        });
        const mallLayer = map.getStyle().layers.find(layer => layer.id === "wailea-mall-logo");
        map.addLayer({
          ...mallLayer, id: "wailea-resort-logos", source: "wailea-resorts",
          layout: { ...mallLayer.layout, "icon-image": ["get", "image"], "icon-padding": 12,
            "icon-size": ["interpolate", ["linear"], ["zoom"], 14, 0.8375, 17.9, 1.25] },
          paint: { ...mallLayer.paint, "icon-opacity": ["interpolate", ["linear"], ["zoom"], 14, 0, 15, ["case", ["boolean", ["feature-state", "hide"], false], 0, 1]] },
        });

        const showroomScale = () => {
          const progress = Math.max(0, Math.min(1, (map.getZoom() - 13) / 4.9));
          return 0.67 + 0.33 * progress;
        };
        let markerScale = showroomScale(),
          scaleTarget = markerScale,
          scaleFrame = 0,
          scaleTime = 0;
        map.addLayer({
          id: "showroom-pin",
          type: "symbol",
          source: "showroom-point",
          slot: "top",
          layout: {
            "icon-image": "showroom-pin",
            "icon-padding": 12,
            "icon-anchor": "bottom",
            "icon-allow-overlap": true,
            "icon-ignore-placement": false,
            "icon-pitch-alignment": "viewport",
            "symbol-z-elevate": true,
            "icon-offset": [0, 30],
            "icon-size": markerScale,

          },
          paint: {
            "icon-emissive-strength": 1,
            "icon-opacity": 0,
            "icon-opacity-transition": { duration: 0 },

          },
        });
        const animateMarkerScale = (now) => {
          const elapsed = Math.min(now - scaleTime, 50);
          scaleTime = now;
          markerScale +=
            (scaleTarget - markerScale) * (1 - Math.exp(-elapsed / 90));
          const settled = Math.abs(scaleTarget - markerScale) < 0.0001;
          if (settled) markerScale = scaleTarget;
          if (map.getLayer("showroom-pin"))
            map.setLayoutProperty("showroom-pin", "icon-size", markerScale);
          scaleFrame = settled ? 0 : requestAnimationFrame(animateMarkerScale);
        };
        map.on("zoom", () => {
          scaleTarget = showroomScale();
          if (!scaleFrame) {
            scaleTime = performance.now();
            scaleFrame = requestAnimationFrame(animateMarkerScale);
          }
        });
        map.on("remove", () => cancelAnimationFrame(scaleFrame));
        map.on(
          "mouseenter",
          "showroom-pin",
          () => (map.getCanvas().style.cursor = "pointer"),
        );
        map.on(
          "mouseleave",
          "showroom-pin",
          () => (map.getCanvas().style.cursor = ""),
        );
        map.on("click", "showroom-pin", (e) => {
          const buildings = map.queryRenderedFeatures(e.point, {
            target: { featuresetId: "buildings", importId: "basemap" },
          });
          const altitude = Math.max(
            0,
            ...buildings.map((f) => Number(f.properties.height) || 0),
          );
          const popupContent = document.createElement("div");
          const title = document.createElement("strong");
          title.textContent = "Raiman Rocks";
          const subtitle = document.createElement("div");
          for (const line of ["The Shops At Wailea", "3750 Wailea Alanui Drive", "EW-6, Level 2", "Wailea, Hawaii 96753"]) {
            const row = document.createElement("div");
            row.textContent = line;
            subtitle.append(row);
          }
          popupContent.append(title, subtitle);
          new mapboxgl.Popup({ offset: 42, altitude })
            .setLngLat(showroom)
            .setDOMContent(popupContent)
            .addTo(map);
        });
      } catch (error) {
        fail();
      }
    });
    map.on("error", (event) => {
      // Authorization failures cannot recover through tile retries.
      if ([401, 403].includes(event.error?.status)) fail();
    });

    const loadTimeout = setTimeout(fail, 30000);
    map.getCanvas().addEventListener("webglcontextlost", (event) => {
      event.preventDefault();
      fail();
    }, { signal });
    function fail() {
      if (destroyed) return;
      showUnavailable(host);
      destroy();
    }
    let resizeFrame = 0;
    let mapWidth = mapElement.clientWidth, mapHeight = mapElement.clientHeight;
    const resize = new ResizeObserver(() => {
      if (destroyed || resizeFrame ||
          (mapElement.clientWidth === mapWidth && mapElement.clientHeight === mapHeight)) return;
      resizeFrame = requestAnimationFrame(() => {
        resizeFrame = 0;
        if (destroyed || (mapElement.clientWidth === mapWidth && mapElement.clientHeight === mapHeight)) return;
        mapWidth = mapElement.clientWidth;
        mapHeight = mapElement.clientHeight;
        map.resize();
        if (!introStarted && mapElement.classList.contains("ready")) map.jumpTo(hawaiiCamera());
      });
    });
    resize.observe(mapElement);
    function destroy() {
      if (destroyed) return;
      destroyed = true;
      clearTimeout(loadTimeout);
      abort.abort();
      cancelAnimationFrame(oceanMaskFrame);
      mapElement.style.removeProperty("--rr-ocean-mask");
      cancelAnimationFrame(scrollFrame);
      cancelAnimationFrame(resizeFrame);
      resize.disconnect();
      waileaMarker.remove();
      map.remove();
    }
    return destroy;
  }
  customElements.define("rr-showroom-map", ShowroomMap);
})();
